import React from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import Grid from "@material-ui/core/Grid";
import PrimaryButton from "../../../../../components/Buttons/PrimaryButton";
import {TRANSACTION_TYPE_VALUES} from "../../../constants";
import {
  createInstrumentHandlers,
  createSwitchInHandler,
  getPortfolioInstruments,
  createHandleAddItems,
  prepareRecommendationsForTransactions,
  addRecommendationsToTransactions,
} from "./tradingOperations";
import {
  computeTransactionValue,
  computeSwitchAmountEuro,
} from "./transactionCalculations";
import {useConfirmationModalContext} from "../../../../../components/ConfirmationModalContextProvider";
import {FUNCTION_NOT_AVAILABLE_MSG} from "../constants";
import RebalancingModal from "./RebalancingModal";


/**
 * Self-contained Rebalancing feature.
 * Owns its own state, button, and modal.
 * No dependency on PortfolioTradeV2 or shared hooks.
 */
const DEFAULT_TRADE_OPTIONS = [
  TRANSACTION_TYPE_VALUES.BUY,
  TRANSACTION_TYPE_VALUES.SELL,
  TRANSACTION_TYPE_VALUES.SWITCH,
];

const RebalancingFeature = ({
  portfolio,
  dataService,
  isVirtual,
  handlePortfolioChange,
  allowedTradeOptions = DEFAULT_TRADE_OPTIONS,
}) => {
  const dispatch = useDispatch();
  const [active, setActive] = React.useState(false);
  const modalContext = useConfirmationModalContext();

  const open = () => setActive(true);
  const close = () => setActive(false);

  const setRecommendations = async (buy, sell) => {
    const switchInHandler = createSwitchInHandler(portfolio, dataService, isVirtual);
    const transactions = portfolio.transactions;

    let switches;
    [buy, sell, switches] = await prepareRecommendationsForTransactions({
      buy, sell, portfolio, allowedTradeOptions, switchOutHandler: handlers[TRANSACTION_TYPE_VALUES.SWITCH],
      switchInHandler,
    });

    const handleTransactionValueChange = (item, value, switchOutItem, withEuro=true) => {
      const result = computeTransactionValue(value, item, switchOutItem, withEuro, portfolio.maxQtyDecimals);
      Object.assign(item, result);
    };

    const handleSwitchAmountEuroChanged = (item, value, switchOutItem) => {
      const result = computeSwitchAmountEuro(value, item, switchOutItem, portfolio.maxQtyDecimals);
      Object.assign(item, result);
    };

    const handleAddItems = createHandleAddItems({
      portfolio, transactions, isVirtual,
      isPassiveDepot: _.get(portfolio, 'data.is_passive'),
      handleTransactionValueChange,
      handleSwitchAmountEuroChanged,
      handlePortfolioChange, dispatch, modalContext,
    });

    await addRecommendationsToTransactions({
      buy, sell, switches, handleAddItems, transactions,
    });

    handlePortfolioChange(portfolio, {transactions: {...transactions}});
    close();
  };

  if (!portfolio) return null;

  const isDisabled = !!portfolio.isModelportfolio;
  const tooltip = isDisabled
    ? FUNCTION_NOT_AVAILABLE_MSG
    : "Rebalancing anhand eines Musterdepots durchführen";

  const handlers = createInstrumentHandlers(portfolio, dataService, isVirtual);

  return (
    <>
      {/* Button */}
      <Grid item>
        <PrimaryButton
          variant="text"
          icon={<i className={"fa fa-balance-scale"} />}
          text="Rebalancing"
          tooltip={tooltip}
          disabled={isDisabled}
          onButtonClick={open}
          dataId={'rebalancing-btn'}
        />
      </Grid>

      <RebalancingModal
        open={active}
        onClose={close}
        portfolioInstruments={getPortfolioInstruments(portfolio, portfolio.transactions, isVirtual, TRANSACTION_TYPE_VALUES.REBALANCING)}
        clearTransactions={() => {
          portfolio.transactions[TRANSACTION_TYPE_VALUES.BUY] = [];
          portfolio.transactions[TRANSACTION_TYPE_VALUES.SELL] = [];
          portfolio.transactions[TRANSACTION_TYPE_VALUES.SWITCH] = [];
        }}
        allTransactions={portfolio.transactions}
        instrumentsHandler={handlers}
        setRebalancingRecommendations={setRecommendations}
        dataService={dataService}
        portfolioData={{name: portfolio.data.name, categoryName: portfolio.data.categoryName}}
        portfolioMarketValue={portfolio.data.market_value}
        withSrri={false}
        clientSRRI={dataService.srri}
      />
    </>
  );
};

export default RebalancingFeature;
